import React, { useEffect, useState } from 'react';

const SubscriptionSuccess = () => {
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('session_id');
  const stripeSuccess = urlParams.get('stripe_success');

  useEffect(() => {
    const processSubscription = async () => {
      if (sessionId || stripeSuccess) {
        console.log('Subscription checkout completed:', { sessionId, stripeSuccess });
        
        // If we have a session_id, verify the payment with the backend
        if (sessionId) {
          try {
            console.log('🔍 Starting payment verification for session:', sessionId);
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const response = await fetch(`${backendUrl}/api/subscription/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ session_id: sessionId })
            });
            
            console.log('🔍 Payment verification response status:', response.status);
            const result = await response.json();
            console.log('🔍 Payment verification result:', result);
            
            if (response.ok && result.success) {
              console.log('✅ Payment verification successful');
            } else {
              console.error('❌ Payment verification failed:', result);
            }
          } catch (error) {
            console.error('💥 Payment verification error:', error);
          }
        }
        
        // Mark as processed
        setIsProcessing(false);
        
        // Auto-redirect to home after 3 seconds with subscription refresh
        const timer = setTimeout(() => {
          // Clear any cached subscription data and redirect to home
          localStorage.removeItem('subscription_cache');
          window.location.href = '/?refresh=' + Date.now();
        }, 3000);

        return () => clearTimeout(timer);
      } else {
        setError('No subscription session found');
        setIsProcessing(false);
      }
    };

    processSubscription();
  }, [sessionId, stripeSuccess]);

  const navigateToDashboard = () => {
    window.location.href = '/?refresh=' + Date.now();
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscription Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={navigateToDashboard}
            className="w-full py-3 px-6 rounded-xl font-semibold text-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Processing Subscription...</h2>
          <p className="text-gray-600 mb-6">Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        {/* Success Message */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Welcome to Math Campus Standard! 🎉
        </h2>
        
        <p className="text-gray-600 mb-6">
          Your subscription has been activated successfully. You now have access to all premium features including Learning Paths and unlimited Practice Sessions.
        </p>

        {/* Features List */}
        <div className="bg-blue-50 rounded-xl p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-3">What's unlocked:</h3>
          <div className="space-y-2 text-left">
            <div className="flex items-center text-sm text-blue-800">
              <svg className="w-4 h-4 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Full access to Learning Paths
            </div>
            <div className="flex items-center text-sm text-blue-800">
              <svg className="w-4 h-4 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Unlimited Practice Sessions
            </div>
            <div className="flex items-center text-sm text-blue-800">
              <svg className="w-4 h-4 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Progress Tracking & Analytics
            </div>
            <div className="flex items-center text-sm text-blue-800">
              <svg className="w-4 h-4 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              14-day free trial
            </div>
          </div>
        </div>

        {/* Trial Info */}
        <div className="bg-yellow-50 rounded-xl p-4 mb-6 border border-yellow-200">
          <p className="text-sm text-yellow-800">
            <span className="font-semibold">🎁 14-Day Free Trial Active!</span><br />
            You won't be charged until your trial ends. Cancel anytime.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={navigateToDashboard}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
          >
            Go to Dashboard
          </button>
          
          <p className="text-xs text-gray-500">
            Redirecting automatically in 5 seconds...
          </p>
        </div>

        {/* Session ID for debugging */}
        {sessionId && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-2">
              Debug Info:
            </p>
            <p className="text-xs text-gray-400">
              Session: {sessionId.substring(0, 20)}...
            </p>
            <p className="text-xs text-gray-400">
              Redirect URL: {window.location.href}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;